<template>
  <v-card class="mx-7" flat>
    <v-card-title>
      {{ questionnaire.description }}

      <v-spacer />

      <v-btn depressed color="primary" @click="download(questionnaireDoc)">
        download
      </v-btn>
    </v-card-title>

    <!-- Description Block -->
    <v-card-text class="questionnaire-form">
      <template v-if="questionnaire.metadata">
        <div class="mb-3">
          <span>
            {{ showAll ? questionnaire.metadata : truncatedMetadata }}
          </span>

          <span
            class="blue--text text-caption ml-1"
            style="cursor: pointer"
            @click="toggleReadMore"
          >
            {{ showAll ? "Read less" : "Read more" }}
          </span>
        </div>

        <v-divider />
      </template>

      <!-- Questionnaire Form -->
      <v-form v-model="isValid" ref="qForm">
        <div v-for="(s, code) in sections" :key="code">
          <v-card-title> {{ s.code }}: &thinsp; {{ s.metadata }} </v-card-title>

          <!-- Parts -->
          <div v-for="p in parts(s.code)" :key="p.code">
            <v-card-subtitle class="text-h5">
              {{ p.metadata }}
            </v-card-subtitle>

            <!-- Questions -->
            <v-card-text v-for="q in questions(s.code, p.code)" :key="q.code">
              <div class="d-flex">
                <span class="text-h6"> {{ q.code }}.</span>
                &ensp;
                <span class="text-h6"> {{ q.question }}</span>
                <span v-if="!q.Optional" class="error--text text-h5">*</span>

                <v-spacer />

                <div v-if="q.maxSelections >= 1" class="ma-auto">
                  {{ q.minSelections }}
                  <span v-if="q.maxSelections !== q.minSelections">
                    &thinsp;-&thinsp;{{ q.maxSelections }}
                  </span>
                  selection(s)
                </div>
              </div>

              <!-- Options -->
              <v-card-text v-if="q.type === 'Options'">
                <template v-if="q.maxSelections > 1">
                  <v-checkbox
                    v-for="(o, i) in q.questionChoices"
                    :key="i"
                    :label="o.description"
                    :value="o"
                    multiple
                    @change="insertChoice($event, 'multiple', q, o.code)"
                  />
                </template>

                <template v-else>
                  <v-radio-group column class="ml-2">
                    <v-radio
                      v-for="(o, i) in q.questionChoices"
                      :key="i"
                      :label="o.description"
                      :value="o"
                      dense
                      @change="insertChoice(o, 'single', q, o.code)"
                    />
                  </v-radio-group>
                </template>
              </v-card-text>

              <!-- Text Input -->
              <v-card-text v-if="q.type === 'Text'">
                <v-textarea
                  outlined
                  rows="2"
                  dense
                  auto-grow
                  maxlength="250"
                  hint="250 characters max"
                  persistent-placeholder
                  placeholder="Type your feedback here..."
                  :rules="!q.Optional ? rules.required : []"
                  @input="insertAnswer($event, q)"
                />
              </v-card-text>
            </v-card-text>
          </div>

          <v-divider />
        </div>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-btn block color="primary" @click="submit">
        Submit Feedback
        <v-icon class="mx-2">chat</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import FileMixin from "../mixins/FileMixin";
import helpers from "../helpers/helper";
import RouterMixin from "../mixins/RouterMixin";

export default {
  name: "FeedbackCard",
  mixins: [FileMixin, RouterMixin],

  data() {
    return {
      isValid: false,
      showAll: false,
      formData: [],
    };
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const code = vm.decodeRoute(to.params.code);
      vm.$store.dispatch("Feedback/getQuestion", code);
      vm.$store.dispatch("Feedback/getQuestionnaireDocument", { docNo: code });
    });
  },

  mounted() {
    Event.$on("fetchDocument", () => {
      this.$router.replace({
        name: "FeedbackDocument",
        params: {
          docNo: this.$route.params.docNo,
          code: this.$route.params.code,
          reference: this.$route.params.reference,
        },
      });
    });
  },

  computed: {
    questionnaire() {
      return this.$store.getters["Feedback/feedbackGetters"]("question");
    },

    questionnaireDoc() {
      return this.$store.getters["Feedback/feedbackGetters"](
        "questionnaireDocument"
      );
    },

    sections() {
      return this.questionnaire.questionnaireSections;
    },

    truncatedMetadata() {
      if (!this.questionnaire.metadata) return "";
      return this.questionnaire.metadata.substring(0, 300) + " ...";
    },

    rules() {
      return { required: [(v) => !!v || "field is required"] };
    },

    maxQuestions() {
      return this.questionnaire.questionnaireQuestions.filter(
        (q) => !q.Optional
      ).length;
    },
  },

  methods: {
    toggleReadMore() {
      this.showAll = !this.showAll;
    },

    parts(section) {
      return this.questionnaire.questionnaireParts.filter(
        (p) => p.section === section
      );
    },

    questions(section, part) {
      return this.questionnaire.questionnaireQuestions.filter(
        (q) => q.section === section && q.part === part
      );
    },

    // Normalize Reference Values
    applyReference(item) {
      item.referenceHeader = this.decodeRoute(this.$route.params.docNo);
      item.reference = this.decodeRoute(this.$route.params.reference);
      item.questionnaireCode = this.decodeRoute(this.$route.params.code);
    },

    insertAnswer(value, q) {
      this.applyReference(q);
      q.answer = value;

      const existing = this.formData.find(
        (i) => i.code === q.code && i.section === q.section && i.part === q.part
      );

      if (!existing) this.formData.push(q);
    },

    insertChoice(event, type, q, code) {
      const found = this.formData.find(
        (i) => i.code === q.code && i.section === q.section && i.part === q.part
      );

      const normalize = (choice) => {
        this.applyReference(choice);
        choice.choice = choice.code;
        return choice;
      };

      // Question already answered
      if (found) {
        if (type === "multiple") {
          event.forEach(normalize);

          const index = found.answerChoices.findIndex((i) => i.code === code);

          index !== -1
            ? found.answerChoices.splice(index, 1)
            : found.answerChoices.push(event[0]);
        } else {
          found.answerChoices = [normalize(event)];
        }
        return;
      }

      // First time answering
      q.answerChoices = Array.isArray(event)
        ? event.map(normalize)
        : [normalize(event)];

      this.applyReference(q);
      this.formData.push(q);
    },

    submit() {
      if (!this.isValid) return this.$refs.qForm.validate();

      const payload = JSON.parse(JSON.stringify(this.formData));

      // Normalize before sending
      payload.forEach((item) => {
        this.applyReference(item);
        item.question = item.code;
      });

      const invalid = payload.find(
        (i) =>
          i.type === "Options" &&
          (i.answerChoices.length < i.minSelections ||
            i.answerChoices.length > i.maxSelections)
      );

      if (invalid) {
        return Event.$emit(
          "ApiWarning",
          `Question ${invalid.code} requires between ${invalid.minSelections} and ${invalid.maxSelections} selections`
        );
      }

      if (payload.filter((i) => !i.Optional).length !== this.maxQuestions) {
        return Event.$emit(
          "ApiWarning",
          "Kindly fill in all the required questions"
        );
      }

      this.$store.dispatch("Feedback/giveFeedback", payload);
    },

    download(base64) {
      const a = document.createElement("a");
      const mime = helpers.detectMimeType(base64);
      const ext = helpers.fileExtension(base64);

      a.href = `data:${mime};base64,${base64}`;
      a.download = `Attachment.${ext}`;
      a.click();
    },
  },
};
</script>

<style scoped>
@import "../styles.css";
</style>
